import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Enum, Pagination, Transaction, Wallet } from '../../../interfaces';
import { WalletSummary } from '../walletSummary/walletSummary';
import { IRTWithdrawal } from './irtWithdrawal/irtWithdrawal.index';
import { ColumnType } from 'antd/lib/table';
import CoinIcon from '../../../utilities/components/coinIcon/coinIcon.index';
import { Badge, Button, Table } from '../../components';
import { BsFillInfoSquareFill } from 'react-icons/bs';
import { getTransactions } from '../../reports/reports.service';
import { ConfigProvider, Divider, Pagination as AntPagination } from 'antd';
import fa_IR from 'antd/locale/fa_IR';
import { getTransactionStates } from '../../../services';
import { ReportDetailModal } from '../../reports/reportDetailModal/reportDetailModal.index';
import { getTransactionBadgeType } from '../../../scripts';
import { ReactComponent as InfoIcon } from 'assets/icons/Info.svg';
import { BankDisruptionModal } from '../../components/bankDisruptionModal/bankDisruptionModal.index';

enum WithdrawalType {
    IRT,
}

const withdrawalTypes = [
    {
        type: WithdrawalType.IRT,
        title: 'برداشت تومانی',
    },
];

interface Props {
    wallet?: Wallet;
    hideBalances: boolean;
    setHideBalances: Dispatch<SetStateAction<boolean>>;
}

export const Withdrawal: FC<Props> = (props) => {
    const [selectedWithdrawalType, setSelectedWithdrawalType] = useState<WithdrawalType>(WithdrawalType.IRT);
    const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [filtersLoading, setFiltersLoading] = useState<boolean>(true);
    const [transactionStates, setTransactionStates] = useState<Enum[]>();
    const [selectedTransactionState, setSelectedTransactionState] = useState<Enum>();
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();
    const [transactions, setTransactions] = useState<Pagination<Transaction>>();
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const renderWithdrawals = () => {
        switch (selectedWithdrawalType) {
            case WithdrawalType.IRT:
                return <IRTWithdrawal {...props} />;
        }
    };

    const handleTransactionDetail = (item: Transaction) => {
        setSelectedTransaction(item);
        setDetailModalOpen(true);
    };

    const handlePageChange = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const buildQueryString = () => {
        const params = new URLSearchParams();
        params.append('type', '2'); // برداشت - ثابت

        if (selectedTransactionState) {
            params.append('state', selectedTransactionState.id.toString());
        }

        return params.toString();
    };

    const fetchData = (page: number, pageSize: number) => {
        const queryString = buildQueryString();

        const finalQueryString = queryString ? `?${queryString}` : '';

        getTransactions(setLoading, setTransactions, page, pageSize, finalQueryString);
    };

    const columns: ColumnType<any>[] = [
        {
            title: 'ردیف',
            dataIndex: 'id',
            key: 'id',
            render: (text, record: Transaction, i) => <span className={'font-medium text-sm'}>{i + 1}</span>,
        },
        {
            title: 'ارز',
            dataIndex: 'market',
            key: 'market',
            render: (pair, record: Transaction) => (
                <div className={'flex items-center gap-3'}>
                    <CoinIcon symbol={record?.market.symbol} className={'w-8 h-8'} />
                    <span className={'text-sm font-bold'}>{record?.market.name}</span>
                    <span className={'text-xs font-medium'}>{record?.market?.symbol?.toUpperCase()}</span>
                </div>
            ),
        },
        {
            title: 'مقدار ارز',
            dataIndex: 'amount',
            key: 'amount',
            render: (text, record: Transaction) => (
                <span className={'font-medium text-sm'}>
                    {record.amount.toLocaleString()} {record.market.symbol}
                </span>
            ),
        },
        // {
        //     title: 'قیمت واحد',
        //     dataIndex: 'onPrice',
        //     key: 'onPrice',
        //     render: (text, record: Transaction) => (
        //         <span className={'font-medium text-sm'}>{record.onPrice.toLocaleString()} تومان</span>
        //     ),
        // },
        {
            title: 'نوع تراکنش',
            dataIndex: 'type',
            key: 'type',
            render: (text, record: Transaction) => <span className={'font-medium text-sm'}>{record.type.title}</span>,
        },
        {
            title: 'زمان',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text, record: Transaction) => (
                <div className={'flex items-center gap-1'}>
                    <span className={'font-medium text-sm'}>{new Date(record.createdAt).toLocaleDateString('fa')}</span>
                    <span className={'font-medium text-xs text-black-60'}>
                        {new Date(record.createdAt).toLocaleTimeString('fa')}
                    </span>
                </div>
            ),
        },
        {
            title: 'وضعیت',
            dataIndex: 'state',
            key: 'state',
            render: (text, record: Transaction) => (
                <Badge
                    type={getTransactionBadgeType(record.state.id) as 'success' | 'warning' | 'danger' | 'info'}
                    title={record.state.title}
                />
            ),
            align: 'center',
        },
        {
            title: 'جزئیات',
            dataIndex: 'details',
            key: 'details',
            render: (text, record: Transaction) => (
                <InfoIcon className={'text-primary-20 clickable'} onClick={() => handleTransactionDetail(record)} />
            ),
        },
    ];

    useEffect(() => {
        getTransactionStates(setFiltersLoading, setTransactionStates);
    }, []);

    useEffect(() => {
        fetchData(page, pageSize);
    }, [page, pageSize, selectedTransactionState]);

    return (
        <>
            <BankDisruptionModal variant="withdrawal" />
            {selectedTransaction && (
                <ReportDetailModal open={detailModalOpen} setOpen={setDetailModalOpen} item={selectedTransaction} />
            )}
            <div className={'flex items-start gap-6 mt-6 max-lg:flex-col-reverse'}>
                <div className={'flex flex-col flex-1 rounded-[8px] border border-primary-20 bg-white max-lg:w-full'}>
                    <div
                        className={
                            'flex items-center rounded-t-[8px] border-b border-b-primary-20  h-14 px-8 gap-6 max-lg:h-12 max-lg:gap-4 max-lg:px-5'
                        }
                    >
                        {withdrawalTypes.map((withdrawal) => (
                            <div
                                className={'relative clickable h-full flex items-center'}
                                key={withdrawal.type}
                                onClick={() => setSelectedWithdrawalType(withdrawal.type)}
                            >
                                <span
                                    className={`transition duration-300 ease-in-out ${selectedWithdrawalType === withdrawal.type ? 'text-black-100' : 'text-black-60'}`}
                                >
                                    {withdrawal.title}
                                </span>
                                {selectedWithdrawalType === withdrawal.type && (
                                    <div className="absolute left-0 right-0 bottom-0 h-1 bg-primary-100 rounded-t-[2.5px]" />
                                )}
                            </div>
                        ))}
                    </div>
                    {renderWithdrawals()}
                </div>
                <div className={'flex flex-1 flex-col max-lg:w-full'}>
                    <WalletSummary {...props} />
                </div>
            </div>

            <div
                className={
                    'flex max-lg:flex-col justify-between items-center mt-6 w-full bg-white h-14 px-4 border border-primary-20 rounded-t-[8px] max-lg:rounded-[8px] max-lg:h-auto'
                }
            >
                <div
                    className={
                        'flex bg-white items-center gap-4 text-sm font-medium relative h-full justify-center overflow-x-scroll scrollbar-hidden max-lg:gap-4 max-lg:justify-start max-lg:w-full max-lg: whitespace-nowrap'
                    }
                >
                    {transactionStates?.map((state) => (
                        <div
                            key={state.id}
                            className="relative clickable h-full flex items-center"
                            onClick={() => {
                                setSelectedTransactionState(
                                    selectedTransactionState?.id === state.id ? undefined : state,
                                );
                            }}
                        >
                            <span
                                className={`py-2 px-4 transition duration-300 ease-in-out ${
                                    selectedTransactionState?.id === state.id ? 'text-black-100' : 'text-black-60'
                                }`}
                            >
                                {state.title}
                            </span>
                            {selectedTransactionState?.id === state.id && (
                                <div className="absolute left-0 right-0 bottom-0 h-1 bg-primary-100 rounded-t-[2.5px]" />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <Table
                className={'border-t-0 rounded-t-0 max-lg:hidden'}
                columns={columns}
                dataSource={transactions?.items}
                loading={loading}
                rowKey="id"
                pagination={false}
            />
            <ul className={'flex flex-col gap-4 mt-4 lg:hidden'}>
                {transactions?.items?.slice((page - 1) * pageSize, page * pageSize).map((item) => (
                    <li className={'p-4 rounded-[8px] bg-white border border-primary-20'} key={item?.trackingCode}>
                        <div className={'flex items-center justify-between'}>
                            <div className={'flex items-center gap-2'}>
                                <CoinIcon symbol={item?.market?.symbol} className={'w-8 h-8'} />
                                <span className={'text-sm font-bold'}>{item?.market?.name}</span>
                                <span className={'text-xs font-medium'}>{item?.market.symbol?.toUpperCase()}</span>
                            </div>
                            <Badge
                                type={
                                    item.state.title.includes('ناموفق')
                                        ? 'danger'
                                        : item.state.title.includes('موفق')
                                          ? 'success'
                                          : item.state.title.includes('انتظار')
                                            ? 'info'
                                            : item.state.title.includes('لغو')
                                              ? 'warning'
                                              : undefined
                                }
                                title={item.state.title}
                            />
                        </div>
                        <Divider />
                        <div className={'flex justify-between items-center'}>
                            {/* <div className={'flex flex-col'}>
                                <p className={'text-sm font-bold'}>قیمت واحد:</p>
                                <span className={'text-sm font-medium text-darkPurple-60'} dir={'ltr'}>
                                    {item.onPrice.toLocaleString()} تومان
                                </span>
                            </div> */}
                            <div className={'flex flex-col'}>
                                <p className={'text-sm font-bold'}>مقدار ارز:</p>
                                <span className={'text-sm font-medium text-darkPurple-60'} dir={'ltr'}>
                                    {item.amount.toLocaleString()} {item.market.symbol}
                                </span>
                            </div>
                            <div className={'flex flex-col'}>
                                <p className={'text-sm font-bold'}>نوع تراکنش:</p>
                                <span className={'text-sm font-medium text-darkPurple-60'}>{item.type.title}</span>
                            </div>
                        </div>
                        <div className={'flex items-center justify-between mt-2'}>
                            <div className={'flex flex-col'}>
                                <p className={'text-sm font-bold'}>زمان:</p>
                                <div className={'flex items-center gap-1'}>
                                    <span className={'font-medium text-sm'}>
                                        {new Date(item.createdAt).toLocaleDateString('fa')}
                                    </span>
                                    <span className={'font-medium text-xs text-black-60'}>
                                        {new Date(item.createdAt).toLocaleTimeString('fa')}
                                    </span>
                                </div>
                            </div>
                            <div className={'flex flex-col'}>
                                <Button type={'default'} onClick={() => handleTransactionDetail(item)}>
                                    جزئیات
                                </Button>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            <ConfigProvider locale={fa_IR}>
                <AntPagination
                    current={page}
                    pageSize={pageSize}
                    total={transactions?.pagination?.totalCount}
                    onChange={handlePageChange}
                    className="mt-4 justify-center"
                    style={{ direction: 'ltr' }}
                    showSizeChanger={true}
                    onShowSizeChange={(current, size) => handlePageChange(current, size)}
                />
            </ConfigProvider>
        </>
    );
};
